import { Directory, File, Paths } from 'expo-file-system';
import { shareAsync } from 'expo-sharing';
import UrlDocumentContext from 'features/urlDocumentScreens/UrlDocumentContext/UrlDocumentContext';
import WebUrlScreen from 'features/urlDocumentScreens/WebUrlScreen/WebUrlScreen';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

const TRUTH_IN_LENDING_FILE_NAME =
  'Varo Pay Over Time Truth in Lending Disclosure.html';
const EXPORT_DIRECTORY_NAME = 'truth-in-lending';
const HTML_MIME_TYPE = 'text/html';
const HTML_UNIFORM_TYPE_IDENTIFIER = 'public.html';

const TruthInLending = () => {
  const { documentUrl } = useContext(UrlDocumentContext);
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'truthInLending',
  });
  const [isSharing, setIsSharing] = useState(false);
  const [hasShareFailed, setHasShareFailed] = useState(false);

  const handleSharePress = async () => {
    if (!documentUrl || isSharing) {
      return;
    }

    setIsSharing(true);
    setHasShareFailed(false);

    try {
      // The latest export is kept until the next one because some share targets
      // (e.g. Google Drive on Android) read the file after the share sheet resolves.
      const exportDirectory = new Directory(Paths.cache, EXPORT_DIRECTORY_NAME);
      if (exportDirectory.exists) {
        exportDirectory.delete();
      }
      exportDirectory.create();
      const downloadedFile = await File.downloadFileAsync(
        documentUrl,
        new File(exportDirectory, TRUTH_IN_LENDING_FILE_NAME),
      );
      await shareAsync(downloadedFile.uri, {
        mimeType: HTML_MIME_TYPE,
        UTI: HTML_UNIFORM_TYPE_IDENTIFIER,
        dialogTitle: translatedText('shareDialogTitle'),
      });
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: `PayOverTime TruthInLending share error: ${error}`,
      });
      setHasShareFailed(true);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      <WebUrlScreen
        trailingAccessories={
          documentUrl ? (
            <IconButton
              iconKey="SHARE"
              iconButtonAccessibilityLabel={translatedText('share')}
              iconOnPress={() => void handleSharePress()}
              isIconDisabled={isSharing}
            />
          ) : null
        }
      />
      {hasShareFailed ? (
        <Snackbar
          text={translatedText('shareError')}
          buttonLabel={translatedText('retry')}
          buttonOnPress={() => void handleSharePress()}
          onHide={() => setHasShareFailed(false)}
        />
      ) : null}
    </>
  );
};

export default TruthInLending;
